import React, { useState, useEffect } from 'react';
import { FaCookie, FaTimes, FaCheck, FaInfoCircle } from 'react-icons/fa';
import { getCookie, setCookie } from '../../utils/cookies';
import { useApp } from '../../context/AppContext';
import './cookies-consent.css';

const CookieConsent = () => {
  const { t } = useApp();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    functional: true, // Language, view tracking
    analytics: false // Future use
  });

  useEffect(() => {
    // DIAGNOSTIC: Test if cookies work at all
    console.log('🔍 Cookie Consent Component Mounted');
    console.log('📋 Current document.cookie:', document.cookie);
    console.log('🌐 navigator.cookieEnabled:', navigator.cookieEnabled);
    console.log('🔗 window.location:', window.location.href);
    
    // Try to set a simple test cookie
    document.cookie = "simple_test=working; path=/";
    const testWorked = document.cookie.includes('simple_test');
    console.log('🧪 Simple cookie test:', testWorked ? '✅ PASSED' : '❌ FAILED');
    
    if (!testWorked) {
      console.error('❌ CRITICAL: Browser cannot save cookies!');
      console.error('Please check:');
      console.error('1. Browser cookies are enabled');
      console.error('2. Not in private/incognito mode');
      console.error('3. No extensions blocking cookies');
    }
    
    // Clean up test cookie
    document.cookie = "simple_test=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    
    // Check if user has already made a choice
    const consent = getCookie('cookie_consent');
    console.log('Checking for existing consent cookie:', consent);
    
    if (!consent) {
      console.log('No consent found, showing banner');
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      console.log('Consent found, banner will not show');
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(decodeURIComponent(consent));
        console.log('Loaded preferences:', savedPrefs);
        setPreferences(savedPrefs);
      } catch (error) {
        console.error('Error parsing cookie consent:', error);
        // If parsing fails, show banner again
        setTimeout(() => setShowBanner(true), 1000);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true
    };
    saveConsent(allAccepted);
  };

  const handleDeclineAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false
    };
    saveConsent(onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (prefs) => {
    const encoded = encodeURIComponent(JSON.stringify(prefs));
    // Use skipConsentCheck=true because this IS the consent cookie
    setCookie('cookie_consent', encoded, 365, true);
    
    console.log('Cookie consent saved:', prefs);
    console.log('Encoded cookie value:', encoded);
    
    // If functional cookies are declined, clear them
    if (!prefs.functional) {
      document.cookie = 'user_language=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'viewed_announcements=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      console.log('Cleared functional cookies');
    }
    
    setShowBanner(false);
    // Reload page to apply consent settings
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop - only show when details are expanded */}
      {showDetails && (
        <div className="cookies-consent-backdrop" onClick={() => setShowDetails(false)} />
      )}
      
      {/* Cookie Consent Banner */}
      <div className={`cookies-consent-banner ${showDetails ? 'expanded' : ''}`}>
        <div className="cookies-consent-header">
          <div className="cookies-consent-icon">
            <FaCookie />
          </div>
          <h3 className="cookies-consent-title">
            {t.cookieConsent?.title || 'We Value Your Privacy'}
          </h3>
        </div>

        <div className="cookies-consent-content">
          {!showDetails ? (
            // Simple View
            <div className="cookies-consent-simple">
              <p className="cookies-consent-description">
                {t.cookieConsent?.description || 
                  'We use cookies to improve your experience, remember your language preference, and track announcement views. By clicking "Accept All", you consent to our use of cookies.'}
              </p>
              
              <button 
                className="cookies-consent-details-btn"
                onClick={() => setShowDetails(true)}
              >
                <FaInfoCircle /> {t.cookieConsent?.customize || 'Customize Settings'}
              </button>
            </div>
          ) : (
            // Detailed View
            <div className="cookies-consent-detailed">
              <p className="cookies-consent-intro">
                {t.cookieConsent?.detailsIntro || 
                  'Choose which cookies you want to accept. Necessary cookies are always enabled as they are essential for the website to function.'}
              </p>

              {/* Necessary Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-switch">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                    />
                    <span className="cookie-slider disabled"></span>
                  </label>
                  <div className="cookie-category-info">
                    <h4>{t.cookieConsent?.necessary || 'Necessary Cookies'}</h4>
                    <span className="cookie-required-badge">
                      {t.cookieConsent?.required || 'Required'}
                    </span>
                  </div>
                </div>
                <p className="cookie-category-description">
                  {t.cookieConsent?.necessaryDesc || 
                    'Essential for the website to function properly. These cannot be disabled.'}
                </p>
              </div>

              {/* Functional Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-switch">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={() => togglePreference('functional')}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                  <div className="cookie-category-info">
                    <h4>{t.cookieConsent?.functional || 'Functional Cookies'}</h4>
                  </div>
                </div>
                <p className="cookie-category-description">
                  {t.cookieConsent?.functionalDesc || 
                    'Remember your language preference and track which announcements you\'ve viewed to provide accurate view counts.'}
                </p>
                <ul className="cookie-list">
                  <li><code>user_language</code> - {t.cookieConsent?.langCookie || 'Stores your language preference (EN/RO)'}</li>
                  <li><code>viewed_announcements</code> - {t.cookieConsent?.viewCookie || 'Tracks viewed announcements to prevent duplicate counts'}</li>
                </ul>
              </div>

              {/* Analytics Cookies (Future) */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-switch">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => togglePreference('analytics')}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                  <div className="cookie-category-info">
                    <h4>{t.cookieConsent?.analytics || 'Analytics Cookies'}</h4>
                    <span className="cookie-optional-badge">
                      {t.cookieConsent?.optional || 'Optional'}
                    </span>
                  </div>
                </div>
                <p className="cookie-category-description">
                  {t.cookieConsent?.analyticsDesc || 
                    'Help us understand how visitors use our website (currently not used).'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="cookies-consent-actions">
          {!showDetails ? (
            <>
              <button 
                className="cookie-btn cookie-btn-decline"
                onClick={handleDeclineAll}
              >
                <FaTimes /> {t.cookieConsent?.declineAll || 'Decline All'}
              </button>
              <button 
                className="cookie-btn cookie-btn-accept"
                onClick={handleAcceptAll}
              >
                <FaCheck /> {t.cookieConsent?.acceptAll || 'Accept All'}
              </button>
            </>
          ) : (
            <>
              <button 
                className="cookie-btn cookie-btn-back"
                onClick={() => setShowDetails(false)}
              >
                {t.cookieConsent?.back || 'Back'}
              </button>
              <button 
                className="cookie-btn cookie-btn-decline"
                onClick={handleDeclineAll}
              >
                <FaTimes /> {t.cookieConsent?.declineAll || 'Decline All'}
              </button>
              <button 
                className="cookie-btn cookie-btn-save"
                onClick={handleSavePreferences}
              >
                <FaCheck /> {t.cookieConsent?.savePreferences || 'Save Preferences'}
              </button>
              <button 
                className="cookie-btn cookie-btn-accept"
                onClick={handleAcceptAll}
              >
                <FaCheck /> {t.cookieConsent?.acceptAll || 'Accept All'}
              </button>
            </>
          )}
        </div>

        <div className="cookies-consent-footer">
          <p>
            {t.cookieConsent?.footerText || 'Learn more in our'}{' '}
            <a href="/cookie-policy" target="_blank">
              {t.cookieConsent?.cookiePolicy || 'Cookie Policy'}
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
